import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography
} from '@mui/material';
import { Phone, VpnKey } from '@mui/icons-material';
import {
  KEY_REQUEST_ACTION,
  KEY_REQUEST_DURATION_DEFAULT,
  KEY_REQUEST_DURATION_MAX,
  KEY_REQUEST_DURATION_MIN,
  KEY_REQUEST_SCOPE
} from '../config/keyRequest';
import { dataverseService } from '../services/dataverseService';
import { getDataverseErrorMessage } from '../lib/dataverseErrorHandler';

/**
 * Dialog for granting or revoking Unloc keys via oc_keyrequest + Power Automate.
 */
export default function KeyRequestDialog({
  open,
  onClose,
  scope,
  lock,
  resident,
  onSuccess
}) {
  const [action, setAction] = useState(KEY_REQUEST_ACTION.GRANT);
  const [phones, setPhones] = useState([]);
  const [phonesLoading, setPhonesLoading] = useState(false);
  const [selectedPhone, setSelectedPhone] = useState(null);
  const [durationMinutes, setDurationMinutes] = useState(String(KEY_REQUEST_DURATION_DEFAULT));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const contextLabel = lock?.name || resident?.name || '';
  const isRevoke = action === KEY_REQUEST_ACTION.REVOKE;
  const hideDuration = !isRevoke && selectedPhone?.lagreNokler;

  useEffect(() => {
    if (!open) {
      return;
    }

    setAction(KEY_REQUEST_ACTION.GRANT);
    setSelectedPhone(null);
    setDurationMinutes(String(KEY_REQUEST_DURATION_DEFAULT));
    setSubmitting(false);
    setError(null);
    setSuccessMessage(null);

    let cancelled = false;
    const loadPhones = async () => {
      setPhonesLoading(true);
      try {
        const data = await dataverseService.phoneService.getAll();
        if (!cancelled) {
          setPhones(data);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(getDataverseErrorMessage(loadError, 'Kunne ikke laste telefonregister.'));
          setPhones([]);
        }
      } finally {
        if (!cancelled) {
          setPhonesLoading(false);
        }
      }
    };

    loadPhones();
    return () => {
      cancelled = true;
    };
  }, [open, scope, lock?.lockId, resident?.residentId]);

  const normalizedPreview = selectedPhone?.mobileNumber || null;

  const phoneOptions = useMemo(
    () =>
      phones.map((phone) => ({
        ...phone,
        searchLabel: `${phone.name} ${phone.mobileNumber}`
      })),
    [phones]
  );

  const handleSubmit = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!selectedPhone) {
      setError('Velg en registrert telefon fra listen.');
      return;
    }

    if (!isRevoke && !hideDuration) {
      const duration = Number(durationMinutes);
      if (
        !Number.isFinite(duration) ||
        duration < KEY_REQUEST_DURATION_MIN ||
        duration > KEY_REQUEST_DURATION_MAX
      ) {
        setError(
          `Varighet må være mellom ${KEY_REQUEST_DURATION_MIN} og ${KEY_REQUEST_DURATION_MAX} minutter.`
        );
        return;
      }
    }

    if (isRevoke) {
      const confirmed = window.confirm(
        `Dette trekker tilbake alle Unloc-nøkler for ${normalizedPreview} i prosjektet. Fortsette?`
      );
      if (!confirmed) {
        return;
      }
    }

    setSubmitting(true);

    try {
      const created = await dataverseService.keyRequestService.create({
        action,
        scope,
        mobileNumber: selectedPhone.mobileNumber,
        phoneId: selectedPhone.phoneId,
        durationMinutes: Number(durationMinutes) || KEY_REQUEST_DURATION_DEFAULT,
        lockId: lock?.lockId,
        residentId: resident?.residentId || resident?.id,
        contextLabel
      });

      const result = await dataverseService.keyRequestService.waitForCompletion(created.requestId);

      if (action === KEY_REQUEST_ACTION.REVOKE) {
        setSuccessMessage(`Nøkler er trukket tilbake for ${normalizedPreview}.`);
      } else if (scope === KEY_REQUEST_SCOPE.SINGLE_LOCK) {
        setSuccessMessage(`Nøkkel utstedt til ${normalizedPreview} for ${contextLabel}.`);
      } else {
        const count = result.locksCount ?? 0;
        setSuccessMessage(`Nøkler utstedt til ${normalizedPreview} for ${count} lås(er).`);
      }

      onSuccess?.(result);
    } catch (submitError) {
      setError(getDataverseErrorMessage(submitError, 'Kunne ikke behandle nøkkelforespørselen.'));
    } finally {
      setSubmitting(false);
    }
  };

  const title =
    scope === KEY_REQUEST_SCOPE.SINGLE_LOCK
      ? `Nøkler for lås: ${lock?.name || ''}`
      : `Nøkler for innbygger: ${resident?.name || ''}`;

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <VpnKey color="primary" />
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Velg en registrert telefon. Tildelingen lagres og logges, og utføres av en
          automatisk prosess. Vent på svar før du lukker vinduet.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {successMessage}
          </Alert>
        )}

        {submitting && !successMessage && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Forespørselen er sendt og loggført. Vennligst vent på svar…
          </Alert>
        )}

        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <RadioGroup
            row
            value={String(action)}
            onChange={(event) => setAction(Number(event.target.value))}
          >
            <FormControlLabel
              value={String(KEY_REQUEST_ACTION.GRANT)}
              control={<Radio />}
              label="Gi nøkler"
              disabled={submitting}
            />
            <FormControlLabel
              value={String(KEY_REQUEST_ACTION.REVOKE)}
              control={<Radio />}
              label="Trekk tilbake"
              disabled={submitting}
            />
          </RadioGroup>
        </FormControl>

        <Autocomplete
          options={phoneOptions}
          loading={phonesLoading}
          value={selectedPhone}
          onChange={(_event, value) => setSelectedPhone(value)}
          getOptionLabel={(option) => option.label || ''}
          isOptionEqualToValue={(option, value) => option.phoneId === value.phoneId}
          filterOptions={(options, { inputValue }) => {
            const query = inputValue.trim().toLowerCase();
            if (!query) {
              return options;
            }
            return options.filter((option) => option.searchLabel.toLowerCase().includes(query));
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Registrert telefon"
              placeholder="Søk på navn eller nummer"
              disabled={submitting}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    <Phone fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {phonesLoading ? <CircularProgress color="inherit" size={18} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                )
              }}
            />
          )}
          sx={{ mb: 2 }}
          disabled={submitting}
          noOptionsText={phonesLoading ? 'Laster…' : 'Ingen registrerte telefoner'}
        />

        {selectedPhone?.lagreNokler && !isRevoke && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Denne telefonen har «Lagre nøkler» – varige nøkler utstedes uten tidsbegrensning.
          </Alert>
        )}

        {!isRevoke && !hideDuration && (
          <TextField
            fullWidth
            type="number"
            label="Varighet (minutter)"
            value={durationMinutes}
            onChange={(event) => setDurationMinutes(event.target.value)}
            disabled={submitting}
            inputProps={{ min: KEY_REQUEST_DURATION_MIN, max: KEY_REQUEST_DURATION_MAX }}
          />
        )}

        {isRevoke && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Alle Unloc-nøkler for dette mobilnummeret i prosjektet trekkes tilbake.
          </Alert>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting}>
          {successMessage ? 'Lukk' : 'Avbryt'}
        </Button>
        {!successMessage && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting || phonesLoading}
            startIcon={submitting ? <CircularProgress size={18} color="inherit" /> : <VpnKey />}
          >
            {submitting ? 'Venter på svar…' : isRevoke ? 'Trekk tilbake' : 'Gi nøkler'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
